import { useEffect, useMemo, useRef } from 'react';
import { motion, useDragControls, useMotionValue } from 'framer-motion';
import { useAtomValue, useSetAtom } from 'jotai';

import { panelsAtom, bringPanelToFrontAtom, updatePanelAtom, PanelId } from '@/stores/panels';
import { useResponsivePanel } from '@/hooks/useResponsivePanel';
import styles from './FloatingPanel.module.css';

interface FloatingPanelProps {
  panelId: PanelId;
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}

export function FloatingPanel({ panelId, title, children, onClose }: FloatingPanelProps) {
  const panel = useAtomValue(panelsAtom)[panelId];
  const bringToFront = useSetAtom(bringPanelToFrontAtom);
  const updatePanel = useSetAtom(updatePanelAtom);
  const { viewport } = useResponsivePanel();
  const dragControls = useDragControls();
  const panelRef = useRef<HTMLDivElement | null>(null);

  const x = useMotionValue(panel.position.x);
  const y = useMotionValue(panel.position.y);

  useEffect(() => {
    if (panel.isMaximized) {
      x.set(0);
      y.set(0);
      return;
    }
    x.set(panel.position.x);
    y.set(panel.position.y);
  }, [panel.isMaximized, panel.position.x, panel.position.y, x, y]);

  useEffect(() => {
    if (!panelRef.current) return;

    const observer = new ResizeObserver(() => {
      if (!panelRef.current || panel.isMaximized) return;
      updatePanel({
        id: panelId,
        size: {
          width: panelRef.current.offsetWidth,
          height: panelRef.current.offsetHeight,
        },
      });
    });

    observer.observe(panelRef.current);
    return () => observer.disconnect();
  }, [panelId, panel.isMaximized, updatePanel]);

  const handleDragEnd = () => {
    updatePanel({
      id: panelId,
      position: { x: x.get(), y: y.get() },
    });
  };

  const handleMinimize = () => {
    updatePanel({ id: panelId, isMinimized: !panel.isMinimized });
  };

  const handleMaximize = () => {
    updatePanel({ id: panelId, isMaximized: !panel.isMaximized, isMinimized: false });
  };

  const panelStyle = useMemo(() => {
    if (panel.isMaximized) {
      return {
        width: Math.max(320, viewport.width - 32),
        height: Math.max(320, viewport.height - 32),
        top: 16,
        left: 16,
      };
    }
    return {
      width: panel.size.width,
      height: panel.isMinimized ? 56 : panel.size.height,
    };
  }, [panel.isMaximized, panel.isMinimized, panel.size.height, panel.size.width, viewport]);

  if (!panel.isOpen) return null;

  return (
    <motion.div
      ref={panelRef}
      className={`${styles.panel} ${panel.isMinimized ? styles.minimized : ''} ${
        panel.isMaximized ? styles.maximized : ''
      }`}
      style={{
        zIndex: panel.zIndex,
        x,
        y,
        width: panelStyle.width,
        height: panelStyle.height,
        top: panel.isMaximized ? panelStyle.top : undefined,
        left: panel.isMaximized ? panelStyle.left : undefined,
      }}
      drag={!panel.isMaximized}
      dragControls={dragControls}
      dragListener={false}
      dragMomentum={false}
      onDragEnd={handleDragEnd}
      onPointerDown={() => bringToFront(panelId)}
      role="dialog"
      aria-modal="false"
      aria-label={title}
    >
      <div className={styles.header} onPointerDown={(event) => dragControls.start(event)}>
        <span className={styles.title}>{title}</span>
        <div className={styles.controls}>
          <button className={styles.controlBtn} onClick={handleMinimize} aria-label="Minimize">
            -
          </button>
          <button className={styles.controlBtn} onClick={handleMaximize} aria-label="Maximize">
            []
          </button>
          <button className={styles.controlBtn} onClick={onClose} aria-label="Close">
            x
          </button>
        </div>
      </div>
      {!panel.isMinimized && <div className={styles.content}>{children}</div>}
    </motion.div>
  );
}

export default FloatingPanel;
